import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ConnectedProps, connect, useSelector } from "react-redux";
import moment from "moment";
import { useHistory, useParams } from "react-router-dom";
import { AppDispatch, RootState } from "store";
import {
  actions,
  detailPickerSelector,
  hasPickerWrongFilesSelector,
  hasPickerAllRequiredFilesLoadedSelector,
} from "reducers/detailPicker";
import {
  pickersSelector,
  actions as pendingUserActions,
} from "reducers/pickers";
import { actions as notificationActions } from "reducers/notification";
import {
  PickerType,
  PickerFilesType,
  pickerTabs,
  AccountTypeType,
} from "../pickers/types";
import { PickerFileRequestType } from "./types";
import { DetailPicker } from "./DetailPicker";
import {
  ACTIVE_PICKER_STATUS,
  DATE_FORMATS,
  FILE_EXT,
  PICKERS_MAX_FILE_SIZE,
} from "utils/constants";
import { NotificationStateType } from "reducers/types/notification";
import { FormApi } from "final-form";
import { validationSchema } from "./schemas";
import validator from "utils/validator";
import { toBase64 } from "utils/files";
import { BankType, PickerFileSaveParamsType } from "sagas/types/pickers";
import { PickerWrongFileType } from "reducers/types/detailPicker";
import { FileError } from "component/filesContainer/types";
import { sessionSelector, actions as sessionActions } from "reducers/session";
import { DEFAULT_COUNTRY } from "hooks/useSession";
import { useTranslation } from "react-i18next";
import { BasicTabType } from "@pickit/pickit-components/dist/Tabs/types";
import { dirtyAlert } from "utils/alerts";
import useBasicData, { BasicDataItemsType } from "hooks/useBasicData";
import { CountryCodeType } from "reducers/types/session";
import { regionSelector, actions as regionsActions } from "reducers/regions";

const tabs: Array<BasicTabType & { id: pickerTabs }> = [
  {
    title: "pickers:pending",
    id: "pending",
    icon: "clock",
  },
  {
    title: "pickers:pickers",
    id: "pickers",
    icon: "user-circle",
  },
];

const basicCountryData: { [key in CountryCodeType]: BasicDataItemsType[] } = {
  AR: ["provinces", "regions"],
  CO: ["accountType", "banks", "provinces", "regions"],
  MX: ["banks", "provinces", "regions"],
  CL: ["banks", "provinces", "regions"],
};

const { shortDate, shortISODate } = DATE_FORMATS;

const DetailPickerContainer = (props: ConnectorProps): JSX.Element => {
  const prevValues = useRef<PickerType | null>(null);
  const params: { id?: string } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const [hasComeFromCode, setHasComeFromCode] = useState(false);
  const [hasComeFrom, setHasComeFrom] = useState(false);
  const fiscalNumberRef = useRef<HTMLDivElement | null>(null);

  const { status, vehicle, id, region } = props.pendingUserAdminPicker;
  const { accountType, banks, provinces, regions } = useBasicData(
    basicCountryData[props.countryCode]
  );
  const formRef = useRef<null | FormApi<object, object>>(null);
  const { countryCode } = useSelector(sessionSelector);

  useEffect(() => {
    props.getPendingUserPicker(parseInt(params.id!));

    return () => {
      props.setDirty(false);
      props.resetWrongFiles();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (regions && region.id && !props.fetchingWorkzones)
      props.getWorkzones(region.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.getWorkzones, region, regions]);

  const active = useMemo(
    () => ACTIVE_PICKER_STATUS.includes(status.tag),
    [status]
  );
  const approveSchema = useMemo(
    () =>
      validationSchema(
        vehicle?.type,
        true,
        props.countryCode,
        hasComeFromCode,
        hasComeFrom,
        props.invalidBank,
        props.fiscalNumberDuplicated
      ),
    [
      hasComeFromCode,
      hasComeFrom,
      props.countryCode,
      props.fiscalNumberDuplicated,
      props.invalidBank,
      vehicle?.type,
    ]
  );

  const saveSchema = useMemo(
    () =>
      validationSchema(
        vehicle?.type,
        false,
        props.countryCode,
        hasComeFromCode,
        hasComeFrom,
        false,
        props.fiscalNumberDuplicated
      ),
    [
      hasComeFromCode,
      hasComeFrom,
      props.countryCode,
      props.fiscalNumberDuplicated,
      vehicle?.type,
    ]
  );

  const formatDate = (date: string | undefined = ""): string | null => {
    const format =
      moment(date, shortISODate, true).isValid() && date.length === 10
        ? moment(date).format(shortDate)
        : date;
    return format || null;
  };

  const initialValues = useMemo(() => {
    if (props.bankNameRequested)
      return prevValues.current || props.pendingUserAdminPicker;

    const picker = props.pendingUserAdminPicker;
    const { accountingData, vehicle, personalData } = picker;
    const { address, account } = accountingData;
    const { dateOfBirth } = personalData;
    let comeFrom = personalData.comeFrom;
    let comeFromCode = undefined;
    const isRefered = !!comeFrom && comeFrom.includes("Programa de referidos");
    setHasComeFromCode(isRefered);
    setHasComeFrom(!!comeFrom);
    if (isRefered) {
      const splitedComeFrom = comeFrom
        .replace("<<", "")
        .replace(">>", "")
        .split(" - ");
      comeFrom = splitedComeFrom[0];
      comeFromCode =
        splitedComeFrom[1] === "undefined" ? undefined : splitedComeFrom[1];
    }

    prevValues.current = picker.id
      ? {
          ...picker,
          region: {
            id: picker?.region?.id,
            name:
              regions.find((region) => region.id === picker?.region?.id)
                ?.name || "",
          },
          desiredWorkzones:
            Array.isArray(picker?.desiredWorkzones) 
              ? picker.desiredWorkzones.join(",")
              : picker?.desiredWorkzones,
          personalData: {
            ...personalData,
            dateOfBirth: dateOfBirth?.includes("-")
              ? moment(dateOfBirth, shortISODate).format(shortDate)
              : dateOfBirth,
            comeFrom,
            comeFromCode,
          },
          accountingData: {
            ...accountingData,
            account: {
              ...account,
              bank: {
                name: (account?.bank as BankType)?.name || "",
                tag: (account?.bank as BankType)?.tag || "",
              },
              number: account?.number || "",
            },
            sapInterlocutor: accountingData?.sapInterlocutor || "-",
            address: {
              ...address,
              postalCode: address?.postalCode || "",
              locality: address?.locality || "",
              street: address?.street || "",
              streetNumber: address?.streetNumber || "",
            },
          },
          ranking: {
            ...picker?.ranking,
            fullRanking: picker?.ranking?.fullRanking || false,
          },
          vehicle: {
            ...vehicle,
            patent: vehicle?.patent || undefined,
            expirationDatePolicyVehicle: formatDate(
              vehicle?.expirationDatePolicyVehicle || ""
            ),
            expirationDateIdentificationVehicle: formatDate(
              vehicle?.expirationDateIdentificationVehicle || ""
            ),
            expirationDateDriverLicense: formatDate(
              vehicle?.expirationDateDriverLicense || ""
            ),
          },
        }
      : picker;
    return prevValues.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, props.bankNameRequested, regions, props.pendingUserAdminPicker]);

  const changePage = (page: pickerTabs, isDirty: boolean) => {
    const onClose = () => {
      props.setActualPage(page);
      history.replace(`/${props.countryCode}/pickers?tab=${page}`);
    };
    if (isDirty) props.showNotification(dirtyAlert({ onCancel: onClose }));
    else if (props.wrongFiles) showWrongFilesNotification(onClose);
    else onClose();
  };

  const restartForm = (form: FormApi<object, object>) => {
    form.setConfig("keepDirtyOnReinitialize", false);
    form.reset();
    const { values } = form.getState();
    const { accountingData } = values as PickerType;
    if (accountingData?.account?.number?.length === 22) {
      const prefix = accountingData?.account?.number?.substring(0, 3);
      props.getBankName(prefix);
    }
    form.setConfig("keepDirtyOnReinitialize", true);
  };

  const cancel = (isDirty: boolean, form: FormApi<object, object>) => {
    const restart = () => restartForm(form);
    if (isDirty) showRestoreNotification(restart);
    else restart();
  };

  const goBack = (validate?: boolean, isDirty?: boolean) => {
    const replace = () => history.replace(`/${props.countryCode}/pickers?tab=${props.actualPage}`);
    if (validate) {
      if (isDirty) props.showNotification(dirtyAlert({ onCancel: replace }));
      else if (props.wrongFiles) showWrongFilesNotification(replace);
      else replace();
    } else replace();
  };

  const showRestoreNotification = (onClose: Function) => {
    return props.showNotification(
      dirtyAlert({
        onCancel: onClose,
        message: t("alerts:changesWillBeLostMessage"),
      })
    );
  };

  const showWrongFilesNotification = (onClose: Function) =>
    props.showNotification({
      id: "notification-file-error",
      level: "warning",
      title: t("alerts:withoutSavingTitle"),
      body: t("alerts:withoutSavingMessage"),
      onConfirmLabel: "global:checkErrors",
      onCancelLabel: "global:continue",
      onCancel: onClose,
    });

  const formatValues = (values: PickerType) => {
    const typeId =
      countryCode !== "CO"
        ? 0
        : (values.accountingData?.account?.type as AccountTypeType)?.tag;
    const personalData = values.personalData;
    const comeFrom = hasComeFromCode
      ? `${personalData.comeFrom} - <<${personalData.comeFromCode}>>`
      : personalData.comeFrom;
    return {
      ...values,
      accountingData: {
        ...values.accountingData,
        account: {
          number: values.accountingData?.account?.number,
          type: typeId,
          bank: (values.accountingData?.account?.bank as BankType)?.tag,
        },
      },
      personalData: {
        ...personalData,
        comeFrom,
        comeFromCode: undefined,
      },
      region: {
        id: values.region.id,
        name: values.region.name,
      },
      ranking: {
        fullRanking: values.ranking?.fullRanking || false,
      },
    };
  };

  const scroll = useCallback(() => {
    setTimeout(function () {
      if (fiscalNumberRef.current) {
        window.scrollTo({
          top: fiscalNumberRef.current.offsetTop - 100,
          left: 0,
          behavior: "smooth",
        });
      }
    }, 1);
  }, []);

  const onSubmit = (values: PickerType) => {
    const formattedValues = formatValues(values);
    if (!active) aproveSubmit(formattedValues, goBack);
    else if (props.wrongFiles) {
      showWrongFilesNotification(() =>
        props.postEditPickerRequest(formattedValues, scroll, formRef)
      );
    } else {
      props.postEditPickerRequest(formattedValues, scroll, formRef);
    }
  };

  const aproveSubmit = (values: PickerType, goBack: Function) => {
    props.showNotification({
      id: "notification-approve",
      level: "info",
      title: t("picker:approvePickerTitle"),
      body: t("picker:approvePickerMessage"),
      onConfirmLabel: "picker:approve",
      onCancelLabel: "picker:revise",
      onConfirm: () => props.postAprovePickerRequest(values, goBack, scroll),
    });
  };

  const savePicker = (values: PickerType) => {
    const sendValues = formatValues(values);
    props.wrongFiles
      ? showWrongFilesNotification(() =>
          props.postPendingUserDocumentsEdit(sendValues, scroll, formRef)
        )
      : validator({
          values: sendValues,
          validationSchema: saveSchema,
          onClick: () =>
            props.postPendingUserDocumentsEdit(sendValues, scroll, formRef),
        });
  };

  const checkFileError = (file: File): FileError | null => {
    if (!FILE_EXT.includes(file.type)) return "FORMAT";
    if (file.size > PICKERS_MAX_FILE_SIZE) return "SIZE";
    return null;
  };

  const onDeleteFile = (tag: PickerFilesType) => {
    props.deleteFile({ id: id, tag });
  };

  const onOpenFile = (tag: PickerFilesType) => {
    props.openFile({ id: id, tag });
  };

  const saveFile = async (tag: PickerFilesType, file: File) => {
    const error = checkFileError(file);
    if (error !== null) {
      props.setFileError({
        tag,
        error,
      });
      return;
    }

    try {
      const base64 = (await toBase64(file)) as string;
      props.saveFile({
        id: id,
        tag,
        content: base64,
      });
    } catch (error) {
      props.setFileError({
        tag,
        error: "LOAD",
      });
    }
  };

  const onUploadFile = (tag: PickerFilesType, file: File) => {
    saveFile(tag, file);
  };

  const onReplaceFile = (tag: PickerFilesType, file: File) => {
    saveFile(tag, file);
  };

  const contactPicker = useCallback(() => {
    const goToWhatsapp = () =>
      window.open(
        `https://api.whatsapp.com/send/?phone=${props.phoneNumber}&text=${t(
          "picker:contactText",
          {
            name: props.pendingUserAdminPicker.personalData.name,
          }
        )}&type=phone_number&app_absent=0`
      );
    if (props.dirty) {
      props.showNotification(
        dirtyAlert({
          onCancel: () => {},
          message: t("picker:dirtyContactText"),
          title: t("picker:dirtyContactTitle"),
          cancelLabel: t("global:back"),
          confirmLabel: t("global:save"),
        })
      );
    } else {
      goToWhatsapp();
    }
  }, [props, t]);

  return (
    <DetailPicker
      {...props}
      banks={banks}
      provinces={provinces}
      accountTypes={accountType}
      regions={regions}
      tabs={tabs}
      hasComeFromCode={hasComeFromCode}
      hasComeFrom={hasComeFrom}
      initialValues={initialValues}
      validationSchema={approveSchema}
      fiscalNumberRef={fiscalNumberRef}
      active={active}
      onSubmit={onSubmit}
      savePicker={savePicker}
      changePage={changePage}
      cancel={cancel}
      goBack={goBack}
      formatDate={formatDate}
      onDeleteFile={onDeleteFile}
      onOpenFile={onOpenFile}
      onUploadFile={onUploadFile}
      onReplaceFile={onReplaceFile}
      contactPicker={contactPicker}
      formRef={formRef}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  dirty: sessionSelector(state).dirtyForm,
  pendingUserAdminPicker: detailPickerSelector(state).pendingUserAdminPicker,
  invalidBank: detailPickerSelector(state).invalidBank,
  fiscalNumberDuplicated: detailPickerSelector(state).fiscalNumberDuplicated,
  bankNameRequested: detailPickerSelector(state).bankNameRequested,
  actualPage: pickersSelector(state).actualPage,
  nameDisplay: detailPickerSelector(state).nameDisplay,
  phoneNumber: detailPickerSelector(state).phoneNumber,
  wrongFiles: hasPickerWrongFilesSelector(state),
  loadedFiles: hasPickerAllRequiredFilesLoadedSelector(state),
  countryCode: sessionSelector(state).countryCode || DEFAULT_COUNTRY,
  workzones: regionSelector(state).workzones,
  fetchingWorkzones: regionSelector(state).fetchingWorkzones,
  hasSaved: detailPickerSelector(state).hasSaved,
  pickerTermsStatus: detailPickerSelector(state).pickerTermsStatus,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getBankName: (cbuPrefix?: string) => {
    dispatch(actions.getBankNameFetch({ cbuPrefix }));
  },
  getPendingUserPicker: (params: number) => {
    dispatch(actions.getPendingUserPickerRequest(params));
  },
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  postAprovePickerRequest: (
    params: PickerType,
    goBack: Function,
    scrollTo: () => void
  ) => {
    dispatch(actions.getAprovePickerRequest({ params, goBack, scrollTo }));
  },
  postPendingUserDocumentsEdit: (
    params: PickerType,
    scrollTo: () => void,
    formRef: React.MutableRefObject<FormApi<object, object> | null>
  ) => {
    dispatch(
      actions.getPendingUserPickerDocumentsEditRequest({
        params,
        scrollTo,
        formRef,
      })
    );
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  postEditPickerRequest: (
    params: PickerType,
    scrollTo: () => void,
    formRef: React.MutableRefObject<FormApi<object, object> | null>
  ) => {
    dispatch(actions.getEditPickerRequest({ params, scrollTo, formRef }));
  },
  setActualPage: (page: pickerTabs) => {
    dispatch(pendingUserActions.setActualPage(page));
  },
  resetWrongFiles: () => {
    dispatch(actions.resetWrongFiles());
  },
  openFile: (params: PickerFileRequestType) => {
    dispatch(actions.getPickerFileRequest(params));
  },
  deleteFile: (params: PickerFileRequestType) => {
    dispatch(actions.getPickerFileDeleteRequest(params));
  },
  saveFile: (params: PickerFileSaveParamsType) => {
    dispatch(actions.getPickerFileSaveRequest(params));
  },
  setFileError: (params: PickerWrongFileType) => {
    dispatch(actions.setWrongFile(params));
  },
  getWorkzones: (regionId: string) => {
    dispatch(regionsActions.getWorkzonesFetch(regionId));
  },
  setFiscalNumberDuplicated: (params: boolean) => {
    dispatch(actions.setFiscalDuplicated(params));
  },
  setHasSaved: (params: boolean) => {
    dispatch(actions.setHasSaved(params));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(DetailPickerContainer);
